import { useShallow } from 'zustand/react/shallow'
import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router'
import { useSearchModalStore } from '@/stores/searchModal'
import { useAuthStore } from '@/stores/auth'
import { useProfilesStore } from '@/stores/profiles'
import { useOrgInvitesStore } from '@/stores/orgInvites'
import { useOrgNotesStore } from '@/stores/orgNotes'
import { useMessagingStore } from '@/stores/messaging'
import { useOrgSelectionStore } from '@/stores/orgSelection'
import { getCachedFindHelpData, prefetchFindHelpData, type FindHelpData } from '@/lib/findHelpData'
import { HARDCODED_GUIDES } from '@/components/findhelp/ResourceGuides'
import { USER_ROLES } from '@/constants/roles'
import { ROUTES } from '@/constants/routes'

interface SearchResult {
  id: string
  label: string
  sublabel?: string
  icon: string
  category: 'Page' | 'Profile' | 'Enrolled' | 'Staff' | 'Note' | 'Message' | 'Community' | 'Find Help'
  action: () => void
}

const COMMUNITY_POSTS = [
  { id: '1', content: 'For meltdowns in public, I carry a small calm kit: headphones, a chewy sensory toy, and one favorite snack. It has saved us so many times.', author: 'Anonymous Parent', type: 'tip' },
  { id: '2', content: 'Liam said "I love you, Mama" for the first time after 3 years of speech therapy. I cried for an hour. Never give up.', author: 'Marites, mom of Liam (8)', type: 'win' },
  { id: '3', content: 'Grocery during off-peak hours changed everything for us. Early morning means fewer crowds, fewer announcements, and fewer surprise transitions.', author: 'Anonymous Parent', type: 'tip' },
  { id: '4', content: 'Anyone have experience preparing a child for a new school aide? We want the transition to feel safe and predictable.', author: 'Anonymous Parent', type: 'question' },
  { id: '5', content: 'Some days I feel like I am failing. My child had three meltdowns today, I lost my patience twice, and I cried in the bathroom. If you feel the same, you are not alone.', author: 'Anonymous Parent', type: 'venting' },
  { id: '6', content: 'We made visual transition cards for waiting-room routines. Families can ask their therapist to print a copy before the next session.', author: 'Sunshine Therapy Center', type: 'tip' },
] as const

const FAMILY_PAGES = [
  { id: 'p-home',      label: 'Home',            icon: 'fa-house',                route: ROUTES.HOME,      keywords: ['dashboard'] },
  { id: 'p-profiles',  label: 'Safety Profiles', icon: 'fa-id-card',              route: ROUTES.PROFILES,  keywords: ['profiles', 'medical'] },
  { id: 'p-qr',        label: 'QR Code',         icon: 'fa-qrcode',               route: ROUTES.QR,        keywords: ['card', 'emergency card'] },
  { id: 'p-location',  label: 'Location',        icon: 'fa-location-dot',         route: ROUTES.LOCATION,  keywords: ['map', 'gps', 'tracking'] },
  { id: 'p-sos',       label: 'Emergency SOS',   icon: 'fa-triangle-exclamation', route: ROUTES.SOS,       keywords: ['sos', 'emergency', 'alert'] },
  { id: 'p-mission',   label: 'Mission Control', icon: 'fa-tower-broadcast',      route: ROUTES.MISSION_CONTROL, keywords: ['panic', 'incident', 'alerts'] },
  { id: 'p-sessions',  label: 'Session Feedback', icon: 'fa-video',               route: ROUTES.SESSIONS,  keywords: ['camera', 'video', 'sessions', 'clips', 'notes'] },
  { id: 'p-community', label: 'Community',       icon: 'fa-people-group',         route: ROUTES.COMMUNITY, keywords: ['resources'] },
  { id: 'p-messaging', label: 'Messages',        icon: 'fa-comments',             route: ROUTES.MESSAGING, keywords: ['chat', 'inbox'] },
  { id: 'p-funding',   label: 'Find Help',       icon: 'fa-hand-holding-heart',   route: ROUTES.FIND_HELP, keywords: ['aid', 'programs', 'findhelp'] },
  { id: 'p-settings',  label: 'Settings',        icon: 'fa-gear',                 route: ROUTES.SETTINGS,  keywords: ['account', 'preferences'] },
  { id: 'p-feedback',  label: 'Feedback',        icon: 'fa-bell',                 route: ROUTES.FEEDBACK,  keywords: ['roadmap', 'bugs', 'features'] },
] as const

const ORG_PAGES = [
  { id: 'p-org',          label: 'My Organization',  icon: 'fa-building',             route: ROUTES.ORG,          keywords: ['dashboard', 'overview'] },
  { id: 'p-org-enrolled', label: 'Enrolled',         icon: 'fa-users',                route: ROUTES.ORG_ENROLLED, keywords: ['families', 'clients', 'enrolled'] },
  { id: 'p-qr',           label: 'QR Code',          icon: 'fa-qrcode',               route: ROUTES.QR,           keywords: ['card', 'emergency card'] },
  { id: 'p-org-location', label: 'Location',         icon: 'fa-location-dot',         route: ROUTES.ORG_LOCATION, keywords: ['map', 'gps', 'families'] },
  { id: 'p-org-sos',      label: 'Emergency SOS',    icon: 'fa-triangle-exclamation', route: ROUTES.ORG_SOS,      keywords: ['sos', 'emergency', 'alerts'] },
  { id: 'p-org-notes',    label: 'Session Notes',    icon: 'fa-clipboard',            route: ROUTES.ORG_NOTES,    keywords: ['notes', 'sessions'] },
  { id: 'p-org-camera',   label: 'Session Camera',   icon: 'fa-video',                route: ROUTES.ORG_CAMERA,   keywords: ['camera', 'video', 'recording'] },
  { id: 'p-org-team',     label: 'Team',             icon: 'fa-user-shield',          route: ROUTES.ORG_TEAM,     keywords: ['staff', 'members'] },
  { id: 'p-messaging',    label: 'Messages',         icon: 'fa-comments',             route: ROUTES.MESSAGING,    keywords: ['chat', 'inbox'] },
  { id: 'p-community',    label: 'Community',        icon: 'fa-people-group',         route: ROUTES.COMMUNITY,    keywords: ['resources'] },
  { id: 'p-funding',      label: 'Find Help',        icon: 'fa-hand-holding-heart',   route: ROUTES.FIND_HELP,    keywords: ['aid', 'programs', 'findhelp'] },
  { id: 'p-settings',     label: 'Settings',         icon: 'fa-gear',                 route: ROUTES.SETTINGS,     keywords: ['account', 'preferences'] },
] as const

const ADMIN_PAGES = [
  { id: 'p-cc',       label: 'Command Center',   icon: 'fa-satellite-dish',       route: ROUTES.COMMAND_CENTER, keywords: ['admin', 'command'] },
] as const

export function GlobalSearchModal() {
  const { isOpen, close } = useSearchModalStore(useShallow((s) => ({ isOpen: s.isOpen, close: s.close })))
  const navigate = useNavigate()
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [findHelpData, setFindHelpData] = useState<FindHelpData | null>(getCachedFindHelpData)
  const inputRef = useRef<HTMLInputElement>(null)

  const authRoles = useAuthStore((s) => s.roles)
  const authIsSuperAdmin = useAuthStore((s) => s.isSuperAdmin)
  const authUid = useAuthStore((s) => s.uid)
  const profiles = useProfilesStore((s) => s.profiles)
  const enrollments = useOrgInvitesStore((s) => s.enrollments)
  const enrolledPhotoCache = useOrgInvitesStore((s) => s.enrolledPhotoCache)
  const familyLabel = useOrgInvitesStore((s) => s.familyLabel)
  const orgStaff = useOrgInvitesStore((s) => s.staff)
  const orgNotes = useOrgNotesStore((s) => s.notes)
  const conversations = useMessagingStore((s) => s.conversations)
  const selectOrgFamily = useOrgSelectionStore((s) => s.select)

  const roles = authRoles()
  const isOrgUser = roles.some(r => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)
  const isFamilyUser = roles.some(r => r.role === USER_ROLES.FAMILY_ADMIN || r.role === USER_ROLES.CO_CARER)
  const isSuperAdmin = authIsSuperAdmin()
  const uid = authUid() ?? ''

  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setFocusedIndex(-1)
      setTimeout(() => inputRef.current?.focus(), 50)
      if (!getCachedFindHelpData()) {
        prefetchFindHelpData().then(setFindHelpData).catch(() => {})
      }
    }
  }, [isOpen])

  useEffect(() => { setFocusedIndex(-1) }, [query])

  const results = useMemo((): SearchResult[] => {
    const raw = query.trim().toLowerCase()
    if (raw.length < 2) return []

    const ageMatch = raw.match(/\bage:\s*(\d+)\b/)
    const ageFilter = ageMatch ? parseInt(ageMatch[1], 10) : null
    const q = raw.replace(/\bage:\s*\d+\b/, '').trim()
    if (ageFilter === null && q.length < 2) return []
    // age:N with no text leaves q empty — include('') matches everything
    const hasTextQuery = q.length >= 2

    const out: SearchResult[] = []

    const pages = isOrgUser ? ORG_PAGES : FAMILY_PAGES
    if (!ageFilter) {
      for (const p of pages) {
        if (p.label.toLowerCase().includes(q) || p.keywords.some(k => k.includes(q))) {
          out.push({ id: p.id, label: p.label, icon: p.icon, category: 'Page', action: () => navigate(p.route) })
        }
      }
    }

    if (isSuperAdmin && !ageFilter) {
      for (const p of ADMIN_PAGES) {
        if (p.label.toLowerCase().includes(q) || p.keywords.some(k => k.includes(q))) {
          out.push({ id: p.id, label: p.label, icon: p.icon, category: 'Page', action: () => navigate(p.route) })
        }
      }
    }

    if (isFamilyUser || isSuperAdmin || profiles.length > 0) {
      for (const p of profiles) {
        if (ageFilter !== null && p.age !== ageFilter) continue
        const haystack = [p.firstName, p.nickname, p.fullName, ...(p.conditions ?? [])].filter(Boolean).join(' ').toLowerCase()
        if (q && !haystack.includes(q)) continue
        const hasNick = p.nickname && p.nickname !== p.firstName
        const displayName = hasNick ? `${p.fullName} · ${p.nickname}` : (p.fullName || p.firstName)
        const condStr = (p.conditions ?? []).slice(0, 2).join(', ') || p.relationship || ''
        const sub = [p.age != null ? `Age ${p.age}` : null, condStr || null].filter(Boolean).join(' · ')
        out.push({
          id: `profile-${p.id}`,
          label: displayName,
          sublabel: sub,
          icon: 'fa-id-card',
          category: 'Profile',
          action: () => navigate(`${ROUTES.PROFILE_DETAIL.replace(':id', p.id)}`),
        })
      }
    }

    if (isOrgUser) {
      for (const e of enrollments) {
        const cache = enrolledPhotoCache[`${e.familyUid}_${e.profileId}`]
        if (ageFilter !== null && cache?.age !== ageFilter) continue
        const nick = cache?.nickname
        const familyName = familyLabel(e.familyUid)
        const haystack = [e.profileName, nick, familyName].filter(Boolean).join(' ').toLowerCase()
        if (q && !haystack.includes(q)) continue
        const enrolledSub = [familyName, cache?.age != null ? `Age ${cache.age}` : null].filter(Boolean).join(' · ')
        out.push({
          id: `enrolled-${e.id}`,
          label: e.profileName,
          sublabel: enrolledSub,
          icon: 'fa-child-reaching',
          category: 'Enrolled',
          action: () => { selectOrgFamily(e.profileId, e.familyUid); navigate(ROUTES.ORG_ENROLLED) },
        })
      }

      for (const s of orgStaff) {
        if (hasTextQuery && (s.displayName.toLowerCase().includes(q) || s.email.toLowerCase().includes(q))) {
          out.push({
            id: `staff-${s.uid}`,
            label: s.displayName,
            sublabel: s.email,
            icon: 'fa-user-shield',
            category: 'Staff',
            action: () => navigate(ROUTES.ORG_TEAM),
          })
        }
      }

      if (orgNotes.length > 0) {
        for (const n of orgNotes) {
          if (hasTextQuery && (n.profileName.toLowerCase().includes(q) || n.content.toLowerCase().includes(q))) {
            const enrollment = enrollments.find(e => e.profileId === n.profileId)
            out.push({
              id: `note-${n.id}`,
              label: n.profileName,
              sublabel: `${n.sessionDate} · ${n.content.slice(0, 50)}`,
              icon: 'fa-clipboard',
              category: 'Note',
              action: () => {
                if (enrollment) selectOrgFamily(n.profileId, enrollment.familyUid)
                navigate(ROUTES.ORG_NOTES)
              },
            })
          }
        }
      }
    }

    for (const c of conversations) {
      const otherNames = Object.entries(c.participantNames ?? {})
        .filter(([k]) => k !== uid)
        .map(([, v]) => v)
        .join(', ')
      const haystack = [otherNames, c.profileName, c.orgName, c.lastMessage].filter(Boolean).join(' ').toLowerCase()
      if (hasTextQuery && haystack.includes(q)) {
        out.push({
          id: `conv-${c.id}`,
          label: otherNames || 'Conversation',
          sublabel: c.profileName ?? c.lastMessage?.slice(0, 50),
          icon: 'fa-comments',
          category: 'Message',
          action: () => navigate(ROUTES.MESSAGING_THREAD.replace(':conversationId', c.id)),
        })
      }
    }

    // Community posts
    let communityHits = 0
    for (const post of COMMUNITY_POSTS) {
      if (communityHits >= 3) break
      if (hasTextQuery && (post.content.toLowerCase().includes(q) || post.author.toLowerCase().includes(q) || post.type.toLowerCase().includes(q))) {
        const snippet = post.content.length > 60 ? post.content.slice(0, 60) + '…' : post.content
        out.push({ id: `community-${post.id}`, label: snippet, sublabel: post.author, icon: 'fa-people-group', category: 'Community', action: () => navigate(`${ROUTES.COMMUNITY}?q=${encodeURIComponent(q)}`) })
        communityHits++
      }
    }

    // Hardcoded guides (always available)
    let guideHits = 0
    for (const g of HARDCODED_GUIDES) {
      if (guideHits >= 5) break
      const haystack = [g.title, g.body, g.note ?? '', ...(g.tags ?? []), g.category].join(' ').toLowerCase()
      if (hasTextQuery && haystack.includes(q)) {
        out.push({ id: `guide-${g.id}`, label: g.title, sublabel: g.category, icon: 'fa-book-open', category: 'Find Help', action: () => navigate(`${ROUTES.FIND_HELP}?tab=guides&q=${encodeURIComponent(g.title)}`) })
        guideHits++
      }
    }

    // Find Help: programs, providers, jobs, approved submissions
    const findHelp = findHelpData
    if (findHelp) {
      let findHelpHits = 0
      for (const p of [...findHelp.govPrograms, ...findHelp.ngoPrograms]) {
        if (findHelpHits >= 8) break
        if (hasTextQuery && (p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || (p.tags ?? []).some(t => t.toLowerCase().includes(q)))) {
          out.push({ id: `program-${p.id}`, label: p.name, sublabel: p.description.slice(0, 60), icon: 'fa-hand-holding-heart', category: 'Find Help', action: () => navigate(`${ROUTES.FIND_HELP}?tab=aid&q=${encodeURIComponent(p.name)}`) })
          findHelpHits++
        }
      }
      for (const p of findHelp.providers) {
        if (findHelpHits >= 8) break
        if (hasTextQuery && (p.name.toLowerCase().includes(q) || p.address.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.accessibilityTags.some(t => t.toLowerCase().includes(q)))) {
          out.push({ id: `provider-${p.id}`, label: p.name, sublabel: `${p.category} · ${p.address}`, icon: 'fa-hospital', category: 'Find Help', action: () => navigate(`${ROUTES.FIND_HELP}?tab=specialists&q=${encodeURIComponent(p.name)}`) })
          findHelpHits++
        }
      }
      for (const j of findHelp.jobs) {
        if (findHelpHits >= 8) break
        if (hasTextQuery && (j.title.toLowerCase().includes(q) || j.company.toLowerCase().includes(q) || j.tags.some(t => t.toLowerCase().includes(q)))) {
          out.push({ id: `job-${j.id}`, label: j.title, sublabel: `${j.company} · ${j.location}`, icon: 'fa-briefcase', category: 'Find Help', action: () => navigate(`${ROUTES.FIND_HELP}?tab=jobs&q=${encodeURIComponent(j.title)}`) })
          findHelpHits++
        }
      }
      for (const s of findHelp.approvedSubmissions) {
        if (findHelpHits >= 8) break
        if (hasTextQuery && (s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || (s.category ?? '').toLowerCase().includes(q)) ) {
          const tab = s.type === 'guide' ? 'guides' : s.type === 'program' ? 'aid' : s.type === 'provider' ? 'specialists' : null
          if (!tab) continue
          out.push({ id: `submission-${s.id}`, label: s.title, sublabel: s.description.slice(0, 60), icon: 'fa-lightbulb', category: 'Find Help', action: () => navigate(`${ROUTES.FIND_HELP}?tab=${tab}`) })
          findHelpHits++
        }
      }
    }

    return out.slice(0, 15)
  }, [query, isOrgUser, isFamilyUser, isSuperAdmin, profiles, orgNotes, conversations, findHelpData, navigate, enrollments, enrolledPhotoCache, familyLabel, orgStaff, selectOrgFamily, uid])

  const groupedResults = useMemo(() => {
    const groups = new Map<string, SearchResult[]>()
    for (const r of results) {
      if (!groups.has(r.category)) groups.set(r.category, [])
      groups.get(r.category)!.push(r)
    }
    return Array.from(groups.entries()).map(([category, items]) => ({ category, items }))
  }, [results])

  function selectResult(result: SearchResult) {
    result.action()
    close()
    setQuery('')
    setFocusedIndex(-1)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusedIndex(i => Math.min(i + 1, results.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setFocusedIndex(i => Math.max(i - 1, 0)) }
    if (e.key === 'Enter') { const r = results[focusedIndex] ?? results[0]; if (r) selectResult(r) }
    if (e.key === 'Escape') close()
  }

  if (!isOpen) return null

  const noHeaderRoute =
    location.pathname.startsWith('/panic-watch') ||
    location.pathname.startsWith('/dependent-safety') ||
    location.pathname.startsWith('/aac/')

  return createPortal(
    <div
      className={`fixed inset-0 z-[300] bg-black/50 flex items-start justify-center px-4 ${noHeaderRoute ? 'pt-6' : 'pt-[76px]'}`}
      onClick={e => { if (e.target === e.currentTarget) close() }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <i className="fa-solid fa-magnifying-glass text-slate-400 text-sm flex-shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search profiles, pages… try age:8"
            className="flex-1 text-sm text-slate-800 placeholder-slate-400 bg-transparent focus:outline-none"
          />
          <button
            onClick={close}
            className="text-xs text-slate-400 hover:text-slate-600 border border-slate-200 rounded px-1.5 py-0.5 flex-shrink-0 transition-colors"
          >
            Esc
          </button>
        </div>

        {/* Results */}
        <div className="overflow-y-auto max-h-72">
          {query.trim().length < 2 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              Type at least 2 characters to search
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              No results for "<span className="text-slate-600 font-medium">{query.trim()}</span>"
            </div>
          ) : (
            groupedResults.map(group => (
              <div key={group.category}>
                <div className="px-4 pt-3 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                  {group.category}
                </div>
                {group.items.map(result => {
                  const idx = results.indexOf(result)
                  return (
                    <button
                      key={result.id}
                      type="button"
                      onClick={() => selectResult(result)}
                      onMouseEnter={() => setFocusedIndex(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${idx === focusedIndex ? 'bg-beacon-50' : 'hover:bg-slate-50'}`}
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-beacon-50 text-beacon-600 text-sm flex-shrink-0">
                        <i className={`fa-solid ${result.icon}`} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{result.label}</div>
                        {result.sublabel && <div className="text-xs text-slate-400 truncate">{result.sublabel}</div>}
                      </div>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-4 text-xs text-slate-400">
          <span><kbd className="font-sans">↑↓</kbd> navigate</span>
          <span><kbd className="font-sans">↵</kbd> select</span>
          <span><kbd className="font-sans">Esc</kbd> close</span>
          <span className="ml-auto"><kbd className="font-sans">Ctrl K</kbd> open</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
